import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

type OptionsFilterProps = ColumnFilterProps & {
    forceClustering?: boolean;
};

export const OptionsFilter: React.FC<OptionsFilterProps> = ({label, onChange, filterParams, forceClustering = false}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);

    const getInitialDisableAnimations = useCallback((): boolean => {
        return filterParams.disableAnimations === true;
    }, [filterParams.disableAnimations]);

    const getInitialEnableClustering = useCallback((): boolean => {
        return filterParams.enableClustering === true;
    }, [filterParams.enableClustering]);

    // Applied state (what's currently in the filter)
    const [appliedDisableAnimations, setAppliedDisableAnimations] = useState<boolean>(getInitialDisableAnimations);
    const [appliedEnableClustering, setAppliedEnableClustering] = useState<boolean>(getInitialEnableClustering);

    // Staged state (what the user is selecting)
    const [selectedDisableAnimations, setSelectedDisableAnimations] = useState<boolean>(getInitialDisableAnimations);
    const [selectedEnableClustering, setSelectedEnableClustering] = useState<boolean>(getInitialEnableClustering);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setSelectedDisableAnimations(appliedDisableAnimations);
            setSelectedEnableClustering(appliedEnableClustering);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedDisableAnimations, appliedEnableClustering]);

    const handleToggleDisableAnimations = useCallback(() => {
        setSelectedDisableAnimations(prev => !prev);
    }, []);

    const handleToggleEnableClustering = useCallback(() => {
        setSelectedEnableClustering(prev => !prev);
    }, []);

    const handleApply = useCallback(() => {
        setAppliedDisableAnimations(selectedDisableAnimations);
        setAppliedEnableClustering(selectedEnableClustering);
        onChange({
            disableAnimations: selectedDisableAnimations,
            enableClustering: selectedEnableClustering
        });
        setIsOpen(false);
    }, [selectedDisableAnimations, selectedEnableClustering, onChange]);

    const handleClear = useCallback(() => {
        setSelectedDisableAnimations(false);
        setSelectedEnableClustering(false);
        setAppliedDisableAnimations(false);
        setAppliedEnableClustering(false);
        onChange({disableAnimations: false, enableClustering: false});
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const newDisableAnimations = getInitialDisableAnimations();
        const newEnableClustering = getInitialEnableClustering();
        setAppliedDisableAnimations(newDisableAnimations);
        setSelectedDisableAnimations(newDisableAnimations);
        setAppliedEnableClustering(newEnableClustering);
        setSelectedEnableClustering(newEnableClustering);
    }, [getInitialDisableAnimations, getInitialEnableClustering]);

    const getDisplayLabel = () => {
        const parts = [];
        if (appliedDisableAnimations) parts.push("Animations Off");
        if (appliedEnableClustering || forceClustering) parts.push("Clustering");
        return parts.length > 0 ? parts.join(", ") : "Default";
    };

    const displayLabel = getDisplayLabel();

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                <span className="text-xs text-blue-600 font-normal">
                    ({displayLabel})
                </span>
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute ${position} mt-1 bg-white rounded shadow-lg z-50 w-[250px] flex flex-col`} onClick={(e) => e.stopPropagation()}>
                    <div className="max-h-[300px] overflow-y-auto">
                        <button
                            onClick={handleToggleDisableAnimations}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                        >
                            <input
                                type="checkbox"
                                checked={selectedDisableAnimations}
                                onChange={() => {}}
                                className="cursor-pointer w-4 h-4"
                            />
                            <span>Disable Animations</span>
                        </button>
                        <button
                            onClick={forceClustering ? undefined : handleToggleEnableClustering}
                            disabled={forceClustering}
                            className={`w-full text-left px-4 py-2 font-normal flex items-center gap-2 ${forceClustering ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-100"}`}
                        >
                            <input
                                type="checkbox"
                                checked={selectedEnableClustering || forceClustering}
                                onChange={() => {}}
                                disabled={forceClustering}
                                className={`w-4 h-4 ${forceClustering ? "cursor-not-allowed" : "cursor-pointer"}`}
                            />
                            <span>Enable Clustering</span>
                        </button>
                    </div>
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
